/**
 * Authenticated file download / open helper (Feb 2026 follow-up).
 *
 * The Partner Marketing Resources page (and any future partner-scoped
 * file endpoint) can't use a plain `<a href>` because our JWT lives in
 * `localStorage` and is attached to requests only via the axios
 * interceptor. Native browser downloads bypass axios → no
 * Authorization header → backend correctly returns 401
 * ({"detail":"Not authenticated"}).
 *
 * This helper:
 *   • fetches the file through the authenticated api client
 *   • receives the response as a Blob
 *   • either downloads it (default) or opens it in a new tab
 *   • surfaces a clear toast if the session has expired (401 → redirect
 *     to /partner/login instead of a confusing "Failed to open")
 *
 * Public helper only. No changes to backend auth or the token contract.
 */
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * @param {{
 *   path: string;                          // e.g. "/partner/pws-marketing-kit/flyer.pdf"
 *   mode?: "download" | "open";            // default "download"
 *   filename?: string;                     // used when mode="download"
 *   loadingMessage?: string;               // shows a toast while fetching
 * }} opts
 */
export async function fetchAndOpenAuthed(opts) {
  const { path, mode = "download", filename, loadingMessage } = opts;
  const toastId = loadingMessage ? toast.loading(loadingMessage) : null;
  try {
    const response = await api.get(path, { responseType: "blob" });
    const blob = new Blob(
      [response.data],
      { type: response.data?.type || response.headers?.["content-type"] || "application/octet-stream" },
    );
    const url = URL.createObjectURL(blob);
    if (mode === "open") {
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup was blocked — fall back to a same-tab navigation
        window.location.href = url;
      }
      // Revoke a bit later so the tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || _filenameFromHeaders(response.headers) || _fallbackFilename(path);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
    if (toastId) toast.dismiss(toastId);
    return true;
  } catch (e) {
    if (toastId) toast.dismiss(toastId);
    // If the raw response is a Blob (error payload), read the JSON out
    // so we can surface a real message. Otherwise fall back to the
    // status code.
    let detail = null;
    const status = e?.response?.status;
    try {
      if (e?.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try { detail = JSON.parse(text).detail; } catch { detail = text; }
      } else if (e?.response?.data?.detail) {
        detail = e.response.data.detail;
      }
    } catch { /* swallow */ }

    if (status === 401) {
      toast.error("Your session has expired. Please sign in again.");
      // Redirect after a short delay so the user reads the toast
      setTimeout(() => { window.location.href = "/partner/login"; }, 900);
    } else if (status === 404) {
      toast.error(detail || "That file isn't available for your account.");
    } else {
      toast.error(detail || "Couldn't open the file. Please try again.");
    }
    return false;
  }
}

function _filenameFromHeaders(headers) {
  const raw = headers?.["content-disposition"];
  if (!raw) return null;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(raw);
  return m ? decodeURIComponent(m[1]) : null;
}

function _fallbackFilename(path) {
  const last = (path || "").split("/").filter(Boolean).pop() || "download";
  return last.split("?")[0];
}
